import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  isMfaChallengeRequired,
  isMfaSetupRequired,
  type LoginResponse,
  type MeResponse,
  type PublicUser,
} from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authed'; user: PublicUser; me: MeResponse };

export type SignInOutcome =
  | { kind: 'authed' }
  | {
      kind: 'mfa_challenge_required';
      token: string;
      expiresInSec: number;
      passkeyAvailable: boolean;
      totpAvailable: boolean;
    }
  | { kind: 'mfa_setup_required'; token: string; expiresInSec: number };

interface AuthCtx {
  state: AuthState;
  signIn: (email: string, password: string, otp?: string) => Promise<SignInOutcome>;
  verifyMfaChallenge: (token: string, otp: string) => Promise<void>;
  verifyPasskeyChallenge: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refreshMe = useCallback(async () => {
    try {
      const me = await apiClient.get<{ data: MeResponse }>('/api/v1/auth/me');
      setState({ status: 'authed', user: me.data.user, me: me.data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous' });
      } else {
        setState({ status: 'anonymous' });
      }
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const signIn = useCallback(
    async (email: string, password: string, otp?: string): Promise<SignInOutcome> => {
      const res = await apiClient.post<{ data: LoginResponse }>('/api/v1/auth/login', {
        email,
        password,
        otp,
      });
      if (isMfaSetupRequired(res.data)) {
        // Backend did NOT issue a session — privileged user must enroll
        // MFA first. UI starts with passkey setup and offers TOTP only
        // as the fallback, then asks them to sign in again.
        return {
          kind: 'mfa_setup_required',
          token: res.data.mfa_setup_token,
          expiresInSec: res.data.expires_in_sec,
        };
      }
      if (isMfaChallengeRequired(res.data)) {
        return {
          kind: 'mfa_challenge_required',
          token: res.data.mfa_challenge_token,
          expiresInSec: res.data.expires_in_sec,
          passkeyAvailable: res.data.passkey_available === true,
          totpAvailable: res.data.totp_available !== false,
        };
      }
      await refreshMe();
      return { kind: 'authed' };
    },
    [refreshMe],
  );

  const verifyPasskeyChallenge = useCallback(
    async (token: string): Promise<void> => {
      const start = await apiClient.post<{
        data: { options: Parameters<typeof startAuthentication>[0]['optionsJSON'] };
      }>('/api/v1/auth/passkeys/login/start', {}, { headers: { 'x-mfa-challenge-token': token } });
      const credential = await startAuthentication({ optionsJSON: start.data.options });
      await apiClient.post<{ data: LoginResponse }>(
        '/api/v1/auth/passkeys/login/verify',
        { credential },
        { headers: { 'x-mfa-challenge-token': token } },
      );
      await refreshMe();
    },
    [refreshMe],
  );

  const verifyMfaChallenge = useCallback(
    async (token: string, otp: string): Promise<void> => {
      await apiClient.post<{ data: LoginResponse }>(
        '/api/v1/auth/mfa/challenge/verify',
        { otp },
        { headers: { 'x-mfa-challenge-token': token } },
      );
      await refreshMe();
    },
    [refreshMe],
  );

  const signOut = useCallback(async () => {
    await apiClient.post('/api/v1/auth/logout', {}).catch(() => undefined);
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ state, signIn, verifyMfaChallenge, verifyPasskeyChallenge, signOut }),
    [state, signIn, verifyMfaChallenge, verifyPasskeyChallenge, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}
